// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Tela de Login + Seletor de Grupo
// Fluxo: Login → Seleção de Grupo (Gamma ou Lubpress) → Sistema
// Duas metades: a marca à esquerda diz o que o sistema faz; o acesso à
// direita é sempre claro, como nos portais de transportadora (#254).
// ═══════════════════════════════════════════════════════════════════
import React, { useState } from "react";
import { supabase }   from "../../lib/supabaseClient";
import { GLOBAL_CSS, syne, mono, expandida } from "../../theme/tokens";
import { Icone, ICONES } from "../../components/ui/Icone";
import { veioDeLinkDeSenha, validarNovaSenha } from "../../utils/linkAuth";

// Lido uma vez, no carregamento: o Supabase apaga o hash depois de usá-lo (#386).
const VEIO_DE_LINK = veioDeLinkDeSenha(window.location.hash);

// Cores fixas para o Login — independente do tema: o painel da marca é
// sempre grafite e o de acesso sempre claro.
const L = {
  marca:    "#0f0f11",
  marcaTxt: "#f4f4f1",
  marcaSub: "#a3a3aa",
  marcaBrd: "#2a2a30",
  fundo:    "#ffffff",
  campo:    "#f3f2ee",
  borda:    "#dcdad3",
  texto:    "#18181b",
  sub:      "#52525b",
  muted:    "#71717a",
  red:      "#C8102E",
  redVivo:  "#E82234",
  green:    "#15803d",
  verdeVivo:"#22c55e",
  purple:   "#6d28d9",
};

// ─── Grupos disponíveis ───────────────────────────────────────────
// Adicionar um novo grupo aqui é suficiente — o seletor aparece automaticamente.
const GRUPOS = [
  { id:"gamma",    nome:"Gamma",    desc:"Distribuidora de Lubrificantes",
    sub:"CWB · MGA · CVL · RBP · MRL",            cor:L.red    },
  { id:"lubpress", nome:"Lubpress", desc:"Operação Vibra",
    sub:"PR (Curitiba) · SP (Ribeirão Preto)",    cor:L.purple },
];

// O que o sistema faz — a primeira tela dizia só "GAMMA TMS".
const RECURSOS = [
  [ICONES.documento, "Notas fiscais, embarques e entregas"],
  [ICONES.lupa,      "Auditoria de fatura e CT-e na SEFAZ"],
  [ICONES.caminhao,  "Tabelas de frete e cotação"],
  [ICONES.alerta,    "Ocorrências e indicadores de OTIF"],
];

// Esconde a marca em tela estreita: estilo em linha não tem media query.
const CSS_LOGIN = `
  .login-grade{display:grid;grid-template-columns:minmax(0,1.05fr) minmax(400px,1fr);min-height:100vh}
  @media (max-width:880px){.login-grade{grid-template-columns:1fr}.login-marca{display:none!important}}
  .login-campo:focus-within{border-color:${L.red}!important;box-shadow:0 0 0 3px ${L.red}1f}
`;

// ─── Metade esquerda: a marca ─────────────────────────────────────
function PainelMarca() {
  return (
    <aside className="login-marca" style={{
      position:"relative", overflow:"hidden", background:L.marca, color:L.marcaTxt,
      display:"flex", flexDirection:"column", justifyContent:"space-between",
      padding:"48px 56px", gap:32,
    }}>
      <div style={{ position:"relative" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, fontSize:11, fontWeight:700,
          letterSpacing:".14em", color:L.marcaSub, ...syne }}>
          <span style={{ width:28, height:2, background:L.redVivo }}/>
          GESTÃO DE FRETE
        </div>
        <h1 style={{ margin:"20px 0 0", fontSize:46, lineHeight:1.05, color:L.marcaTxt, ...expandida }}>
          Todo o frete<br/>da Gamma,<br/>
          <span style={{ color:L.redVivo }}>em um só lugar.</span>
        </h1>
        <p style={{ margin:"18px 0 0", fontSize:14, lineHeight:1.6, color:L.marcaSub, maxWidth:440 }}>
          Da nota faturada à fatura da transportadora: confira, compare e cobre
          com os números da operação.
        </p>
        <ul style={{ listStyle:"none", padding:0, margin:"28px 0 0", display:"grid", gap:12 }}>
          {RECURSOS.map(([d, txt]) => (
            <li key={txt} style={{ display:"flex", alignItems:"center", gap:12, fontSize:14, color:L.marcaTxt }}>
              <span style={{ width:34, height:34, borderRadius:9, flexShrink:0,
                display:"inline-flex", alignItems:"center", justifyContent:"center",
                background:L.redVivo + "1f", border:`1px solid ${L.redVivo}40` }}>
                <Icone d={d} size={16} color={L.redVivo} strokeWidth={2}/>
              </span>
              {txt}
            </li>
          ))}
        </ul>
      </div>

      <div style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"space-between",
        gap:12, paddingTop:22, borderTop:`1px solid ${L.marcaBrd}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <img src="/logo.png" alt="" style={{ width:40, height:40, borderRadius:9, objectFit:"contain" }}/>
          <div>
            <div style={{ fontSize:14, color:L.marcaTxt, ...expandida }}>GAMMA TMS</div>
            <div style={{ fontSize:11, color:L.marcaSub, ...mono }}>Gamma · Lubpress</div>
          </div>
        </div>
        <span style={{ display:"inline-flex", alignItems:"center", gap:7, padding:"5px 12px",
          borderRadius:99, border:`1px solid ${L.marcaBrd}`, fontSize:10.5, fontWeight:700,
          letterSpacing:".08em", color:L.marcaSub, ...syne }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:L.verdeVivo }}/>
          SISTEMA OPERACIONAL
        </span>
      </div>
    </aside>
  );
}

// ─── Metade direita: moldura do acesso ────────────────────────────
function PainelAcesso({ titulo, subtitulo, children }) {
  return (
    <main style={{ background:L.fundo, color:L.texto, display:"flex", flexDirection:"column",
      justifyContent:"center", alignItems:"center", padding:"40px 24px" }}>
      <div style={{ width:"100%", maxWidth:380 }}>
        {/* No celular a marca some; o logo fica no topo do acesso. */}
        <img src="/logo.png" alt="Gamma TMS" className="login-logo-mobile"
          style={{ width:44, height:44, borderRadius:9, objectFit:"contain", marginBottom:18 }}/>
        <div style={{ display:"flex", alignItems:"center", gap:10, fontSize:11, fontWeight:700,
          letterSpacing:".14em", color:L.red, ...syne }}>
          ACESSO SEGURO
          <span style={{ width:36, height:1, background:L.borda }}/>
        </div>
        <h2 style={{ margin:"10px 0 0", fontSize:30, lineHeight:1.1, color:L.texto, ...expandida }}>{titulo}</h2>
        <p style={{ margin:"8px 0 26px", fontSize:13.5, color:L.muted }}>{subtitulo}</p>
        {children}
        <div style={{ marginTop:34, fontSize:11.5, color:L.muted, textAlign:"center" }}>
          © 2025–2026 <strong style={{ color:L.sub }}>Gamma Distribuidora de Lubrificantes</strong>
        </div>
      </div>
    </main>
  );
}

function Campo({ id, rotulo, icone, children, acao }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label htmlFor={id} style={{ display:"block", marginBottom:6, fontSize:11, fontWeight:700,
        letterSpacing:".1em", color:L.sub, ...syne }}>{rotulo}</label>
      <div className="login-campo" style={{ display:"flex", alignItems:"center", gap:10,
        background:L.campo, border:`1px solid ${L.borda}`, borderRadius:10, padding:"0 12px",
        transition:"var(--m-cor)" }}>
        <Icone d={icone} size={16} color={L.muted}/>
        {children}
        {acao}
      </div>
    </div>
  );
}

const estiloInput = {
  flex:1, minWidth:0, border:"none", outline:"none", background:"transparent",
  padding:"13px 0", fontSize:14, color:L.texto,
};

// ─── Seletor de grupo (aparece após login bem-sucedido) ───────────
function SeletorGrupo({ user, onSelecionar, onSair }) {
  const [hoverId, setHoverId] = useState(null);
  return (
    <PainelAcesso titulo="Escolha o grupo" subtitulo="Cada grupo abre a sua operação e as suas filiais.">
      <div style={{ display:"grid", gap:10 }}>
        {GRUPOS.map(g => {
          const hover = hoverId === g.id;
          return (
            <button type="button" key={g.id} onClick={() => onSelecionar(g.id)}
              onMouseEnter={() => setHoverId(g.id)} onMouseLeave={() => setHoverId(null)}
              onFocus={() => setHoverId(g.id)} onBlur={() => setHoverId(null)}
              style={{ width:"100%", textAlign:"left", padding:"16px 18px", borderRadius:12,
                cursor:"pointer", display:"flex", alignItems:"center", gap:14,
                border:`1.5px solid ${hover ? g.cor : L.borda}`,
                background: hover ? g.cor + "0d" : L.fundo,
                boxShadow: hover ? `0 6px 18px ${g.cor}1f` : "none",
                transition:"var(--m-cor)" }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:g.cor, flexShrink:0 }}/>
              <span style={{ flex:1 }}>
                <span style={{ display:"block", fontSize:17, color: hover ? g.cor : L.texto, ...expandida }}>{g.nome}</span>
                <span style={{ display:"block", fontSize:12.5, color:L.sub, marginTop:2 }}>{g.desc}</span>
                <span style={{ display:"block", fontSize:11, color:L.muted, marginTop:2, ...mono }}>{g.sub}</span>
              </span>
              <Icone d={ICONES.seta} size={18} color={hover ? g.cor : L.muted} strokeWidth={2}/>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop:20, paddingTop:16, borderTop:`1px solid ${L.borda}`,
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, fontSize:12.5 }}>
        <span style={{ color:L.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          Logado como <strong style={{ color:L.texto }}>{user?.email}</strong>
        </span>
        {/* Chamar signOut() direto aqui não saía: o seletor é renderizado
            por causa do estado LOCAL userLogado, que a sessão do Supabase
            não controla. A sessão encerrava, a tela continuava igual. */}
        <button type="button" onClick={onSair}
          style={{ background:"none", border:"none", color:L.red, fontSize:12.5, fontWeight:700,
            cursor:"pointer", flexShrink:0, ...syne }}>
          Sair
        </button>
      </div>
    </PainelAcesso>
  );
}

// ─── Componente principal de Login ────────────────────────────────
function Login({ onLogin }) {
  // Injetar CSS de tema (Login renderiza antes do App)
  React.useEffect(() => {
    if (!document.getElementById("tms-global-css")) {
      const s = document.createElement("style");
      s.id = "tms-global-css";
      s.textContent = GLOBAL_CSS;
      document.head.appendChild(s);
    }
    document.documentElement.setAttribute("data-theme",
      localStorage.getItem("tms_tema") || "dark");
  }, []);

  // Se o usuário já está autenticado (ex: clicou em "Trocar grupo"),
  // vai direto para o seletor de grupo sem exibir o formulário de login.
  React.useEffect(() => {
    supabase.auth.getSession()
      .then(({ data }) => { if (data.session?.user) setUserLogado(data.session.user); })
      .catch(e => console.error("getSession (Login):", e));
  }, []);

  const [email,   setEmail]   = useState("");
  const [senha,   setSenha]   = useState("");
  const [erro,    setErro]    = useState("");
  const [loading, setLoading] = useState(false);
  const [modo,    setModo]    = useState("login"); // "login" | "recuperar"
  const [sucesso, setSucesso] = useState("");
  // Após autenticar, guarda o usuário aqui para mostrar o seletor de grupo
  const [userLogado, setUserLogado] = useState(null);
  const [verSenha, setVerSenha] = useState(false);
  // Convite e recuperação entram com sessão mas sem senha definida (#386).
  const [definindoSenha, setDefinindoSenha] = useState(VEIO_DE_LINK);
  const [confirmacao, setConfirmacao] = useState("");

  async function salvarSenha(e) {
    e.preventDefault();
    const invalida = validarNovaSenha(senha, confirmacao);
    if (invalida) { setErro(invalida); return; }
    setErro(""); setLoading(true);
    try {
      const { error } = await comTimeout(supabase.auth.updateUser({ password: senha }));
      if (error) { setErro(error.message); return; }
      setSenha(""); setConfirmacao(""); setDefinindoSenha(false);
    } catch (err) {
      console.error("Falha ao definir a senha:", err);
      setErro(err?.message === "__timeout__"
        ? "O servidor não respondeu. Tente novamente."
        : "Falha ao salvar a senha: " + (err?.message || err));
    } finally {
      setLoading(false);
    }
  }

  // Encerra a sessão E limpa o estado local — os dois são necessários: sem o
  // signOut a sessão continua válida no próximo F5; sem o setUserLogado(null)
  // a tela não volta para o formulário. O estado é limpo mesmo se o signOut
  // falhar, senão o usuário fica preso num seletor de grupo que ele já pediu
  // para abandonar.
  async function sair() {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("signOut falhou:", err);
    } finally {
      setUserLogado(null);
      setEmail(""); setSenha(""); setErro(""); setSucesso("");
    }
  }

  // Corre contra um tempo limite: sem isso, uma chamada que nunca responde
  // deixava o botão preso em "Aguarde..." indefinidamente.
  function comTimeout(promessa, ms = 20000) {
    return Promise.race([
      promessa,
      new Promise((_, rej) => setTimeout(() => rej(new Error("__timeout__")), ms)),
    ]);
  }

  async function entrar(e) {
    e.preventDefault();
    setErro(""); setLoading(true);
    try {
      const { data, error } = await comTimeout(
        supabase.auth.signInWithPassword({ email, password: senha }));
      if (error) {
        setErro(error.message === "Invalid login credentials"
          ? "Email ou senha incorretos."
          : error.message);
      } else {
        // Autenticou com sucesso — mostra o seletor de grupo
        // (não chama onLogin ainda)
        setUserLogado(data.user);
      }
    } catch (err) {
      // Antes não havia try/catch: uma exceção aqui pulava o setLoading(false)
      // e o botão ficava travado para sempre, sem nenhuma mensagem.
      console.error("Falha no login:", err);
      setErro(err?.message === "__timeout__"
        ? "O servidor não respondeu. Verifique sua conexão e tente novamente."
        : "Falha ao entrar: " + (err?.message || err));
    } finally {
      setLoading(false);   // sempre libera o botão
    }
  }

  async function recuperar(e) {
    e.preventDefault();
    setErro(""); setLoading(true);
    try {
      const { error } = await comTimeout(
        supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        }));
      if (error) setErro(error.message);
      else setSucesso("Email de recuperação enviado!");
    } catch (err) {
      console.error("Falha na recuperação de senha:", err);
      setErro(err?.message === "__timeout__"
        ? "O servidor não respondeu. Tente novamente."
        : "Falha ao enviar: " + (err?.message || err));
    } finally {
      setLoading(false);
    }
  }

  // As duas metades valem para os três estados: login, recuperação e
  // seletor de grupo. Só o conteúdo do painel de acesso muda (#254).
  const moldura = conteudo => (
    <div className="login-grade" style={{ background:L.fundo }}>
      <style>{CSS_LOGIN + `.login-logo-mobile{display:none}
        @media (max-width:880px){.login-logo-mobile{display:block}}`}</style>
      <PainelMarca/>
      {conteudo}
    </div>
  );

  if (userLogado && definindoSenha) {
    return moldura(
      <PainelAcesso titulo="Defina sua senha"
        subtitulo="Escolha a senha que você vai usar para entrar no Gamma TMS.">
        <form onSubmit={salvarSenha}>
          <Campo id="nova-senha" rotulo="NOVA SENHA" icone={ICONES.cadeado}>
            <input type="password" id="nova-senha" value={senha} autoComplete="new-password"
              onChange={e => setSenha(e.target.value)} required minLength={8}
              placeholder="Mínimo de 8 caracteres" style={estiloInput}/>
          </Campo>
          <Campo id="confirma-senha" rotulo="CONFIRME A SENHA" icone={ICONES.cadeado}>
            <input type="password" id="confirma-senha" value={confirmacao} autoComplete="new-password"
              onChange={e => setConfirmacao(e.target.value)} required
              placeholder="Repita a senha" style={estiloInput}/>
          </Campo>
          {erro && (
            <div role="alert" style={{ marginBottom:14, padding:"10px 12px", borderRadius:9,
              background:L.red + "10", color:L.red, fontSize:13, border:`1px solid ${L.red}33` }}>
              {erro}
            </div>
          )}
          <button type="submit" disabled={loading}
            style={{ width:"100%", marginTop:6, padding:"14px", borderRadius:10, border:"none",
              background: loading ? L.borda : L.red,
              color: loading ? L.muted : "#fff", fontWeight:700, fontSize:14.5,
              cursor: loading ? "default" : "pointer", ...syne }}>
            {loading ? "Aguarde..." : "Salvar e entrar"}
          </button>
        </form>
      </PainelAcesso>
    );
  }

  // Se já autenticou, mostra o seletor de grupo
  if (userLogado) {
    return moldura(
      <SeletorGrupo
        user={userLogado}
        onSelecionar={(grupo) => onLogin(userLogado, grupo)}
        onSair={sair}
      />
    );
  }

  const linkEstilo = { background:"none", border:"none", padding:0, cursor:"pointer",
    fontSize:13, fontWeight:600, color:L.red, ...syne };

  // Formulário de login
  return moldura(
    <PainelAcesso
      titulo={modo === "login" ? "Acesse sua conta" : "Recuperar senha"}
      subtitulo={modo === "login"
        ? "Entre com seu e-mail e senha para continuar."
        : "Informe seu e-mail e enviaremos um link para criar uma nova senha."}>
      <form onSubmit={modo === "login" ? entrar : recuperar}>
        {/* htmlFor/id: sem o vínculo, o leitor de tela caía no
            placeholder — que some quando a pessoa digita (#94). */}
        <Campo id="login-email" rotulo="E-MAIL" icone={ICONES.usuario}>
          <input type="email" value={email} id="login-email" autoComplete="username"
            onChange={e => setEmail(e.target.value)} required
            placeholder="seu@email.com" style={estiloInput}/>
        </Campo>

        {modo !== "recuperar" && (
          <Campo id="login-senha" rotulo="SENHA" icone={ICONES.cadeado}
            acao={
              // Mostrar a senha: conferir o que foi digitado antes de errar
              // três vezes. aria-pressed diz o estado ao leitor de tela.
              <button type="button" onClick={() => setVerSenha(v => !v)}
                aria-label={verSenha ? "Ocultar senha" : "Mostrar senha"} aria-pressed={verSenha}
                title={verSenha ? "Ocultar senha" : "Mostrar senha"}
                style={{ background:"none", border:"none", padding:4, cursor:"pointer",
                  display:"inline-flex", color:L.muted }}>
                <Icone d={verSenha ? ICONES.olhoFechado : ICONES.olho} size={17}/>
              </button>
            }>
            <input type={verSenha ? "text" : "password"} value={senha} id="login-senha"
              autoComplete="current-password"
              onChange={e => setSenha(e.target.value)} required
              placeholder="••••••••" style={estiloInput}/>
          </Campo>
        )}

        {erro && (
          <div role="alert" style={{ marginBottom:14, padding:"10px 12px", borderRadius:9,
            background:L.red + "10", color:L.red, fontSize:13, border:`1px solid ${L.red}33`,
            display:"flex", gap:8, alignItems:"flex-start" }}>
            <Icone d={ICONES.alerta} size={16} color={L.red} strokeWidth={2}/>
            <span>{erro}</span>
          </div>
        )}
        {sucesso && (
          <div role="status" style={{ marginBottom:14, padding:"10px 12px", borderRadius:9,
            background:L.green + "10", color:L.green, fontSize:13, border:`1px solid ${L.green}33` }}>
            {sucesso}
          </div>
        )}

        <button type="submit" disabled={loading}
          style={{ width:"100%", marginTop:6, padding:"14px", borderRadius:10, border:"none",
            display:"flex", alignItems:"center", justifyContent:"center", gap:9,
            background: loading ? L.borda : L.red,
            color: loading ? L.muted : "#fff",
            fontWeight:700, fontSize:14.5,
            cursor: loading ? "default" : "pointer",
            transition:"var(--m-cor)", ...syne }}>
          {!loading && <Icone d={modo === "login" ? ICONES.entrar : ICONES.enviar} size={17} strokeWidth={2}/>}
          {loading ? "Aguarde..." : modo === "login" ? "Entrar" : "Enviar e-mail"}
        </button>
      </form>

      <div style={{ marginTop:18, textAlign:"center" }}>
        {modo !== "login" ? (
          <button type="button" style={linkEstilo}
            onClick={() => { setModo("login"); setErro(""); setSucesso(""); }}>
            ← Voltar ao login
          </button>
        ) : (
          <button type="button" style={{ ...linkEstilo, color:L.sub, fontWeight:500 }}
            onClick={() => { setModo("recuperar"); setErro(""); }}>
            Esqueci minha senha
          </button>
        )}
      </div>
    </PainelAcesso>
  );
}

export default Login;
